import {useCallback} from 'react';

import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';

import {SortableHeaderCellProps, SortIcon} from './SortableHeaderCell';

export type SimpleTableSortDirection = Exclude<SortIcon, null>;

export type SimpleTableSortState = null | {
  columnKey: string | number;
  sortDirection: SimpleTableSortDirection;
};

const simpleTableSortState = makeUseGlobalClientState<SimpleTableSortState>(null);

export interface UseSimpleTableSortStateOutput {
  sortState: SimpleTableSortState;
  /**
   * Toggles the sort state on the input column.
   *
   * If sortDirection is not passed:
   * - If the current sort state is based on the input column,
   *   will toggle between arrow-down -> arrow-up -> null sort direction.
   * - If the current sort state is based on a different column than the input column,
   *   will switch to sorting the input column with arrow-down sort direction.
   */
  onToggleSort: (columnKey: string | number, sortDirection?: SimpleTableSortDirection | null) => void;
  /**
   * Function to generate the props to pass to SortableHeaderCell.
   */
  getHeaderCellProps: (
    columnKey: string | number
  ) => Pick<SortableHeaderCellProps, 'sortIcon' | 'onToggleSort'>;
}

/**
 * Hook to manage sort state for SimpleTable, persisting sort state in Redux.
 *
 * @see SimpleTableStories.tsx > SortableStory
 * @param clientStateId Unique client-side identifier for the table, used to store sort state in Redux.
 */
export default function useSimpleTableSortState(clientStateId: string): UseSimpleTableSortStateOutput {
  const [sortState, setSortState] = simpleTableSortState.useGlobalClientState(clientStateId);

  const onToggleSort: UseSimpleTableSortStateOutput['onToggleSort'] = useCallback(
    (columnKey, sortDirection) => {
      if (sortDirection !== undefined) {
        setSortState(sortDirection ? {columnKey, sortDirection} : null);
      } else if (sortState == null || sortState.columnKey !== columnKey) {
        setSortState({columnKey, sortDirection: 'arrow-down'});
      } else if (sortState.sortDirection === 'arrow-down') {
        setSortState({columnKey, sortDirection: 'arrow-up'});
      } else if (sortState.sortDirection === 'arrow-up') {
        setSortState(null);
      }
    },
    [sortState, setSortState]
  );

  const getHeaderCellProps: UseSimpleTableSortStateOutput['getHeaderCellProps'] = useCallback(
    (columnKey) => ({
      sortIcon: columnKey === sortState?.columnKey ? sortState.sortDirection : null,
      onToggleSort: () => {
        onToggleSort(columnKey);
      },
    }),
    [sortState, onToggleSort]
  );

  return {sortState, onToggleSort, getHeaderCellProps};
}
